import type { MemberDirectoryEntry, Run } from "./types";

// The invite screen (DESIGN_SLICE_INVITE_SCREEN 281026 V1.0): the pure part.
// What the screen names, how it matches an address to the roster, and what
// an invitation's status is — from the run's state and two counts, nothing
// derived of its own. No person is named here: the roster entry's address
// is shown to the administrator who typed it, and nowhere else.

export type InvitationStatus = "sent" | "answering" | "report_ready" | "retired";

export const STATUS_LABEL: Record<InvitationStatus, string> = {
  sent: "Sent",
  answering: "Answering",
  report_ready: "Report ready",
  retired: "Retired",
};

/** The one PMO function of a self-assessment tenant, and the engagement and run named by the day. */
export function defaultNames(today: Date): { entity: string; engagement: string; run: string } {
  const day = today.toISOString().slice(0, 10);
  return {
    entity: "PMO function",
    engagement: `Self-assessment ${day}`,
    run: `Self-assessment ${day}`,
  };
}

/** Exact, case-insensitive match of a typed address against the roster. */
export function matchMember(
  email: string,
  roster: readonly MemberDirectoryEntry[],
): MemberDirectoryEntry | null {
  const wanted = email.trim().toLowerCase();
  if (!wanted) return null;
  return roster.find((m) => m.email.trim().toLowerCase() === wanted) ?? null;
}

/** A plain check that the typed text is shaped like one address. */
export function looksLikeEmail(email: string): boolean {
  const s = email.trim();
  return s.length >= 6 && s.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

/**
 * The invitation's text (DESIGN_SLICE_INVITATION_TEXT 301026 V1.0): the
 * standard greeting, word for word the paragraph the Auth templates print,
 * prefilled in the box and editable for a person new to the roster. The
 * typed text travels as the new identity's sign-up data and the Confirm
 * sign up template prints it; a known person's Magic Link stays standard.
 */
export const INVITATION_TEXT =
  "LINNFOSS has set up an assessment for you. The link below opens it and signs you in; no password is needed.";

export const INVITATION_TEXT_MAX = 1000;

export const KNOWN_PERSON_TEXT = "Already on the roster: the mail carries the standard text.";

/** The text the mail carries: line ends made plain, trimmed, at most the limit; empty gives the standard text. */
export function invitationText(typed: string): string {
  const s = typed.replace(/\r\n?/g, "\n").trim().slice(0, INVITATION_TEXT_MAX).trim();
  return s || INVITATION_TEXT;
}

export const NOT_A_MEMBER =
  "Not on the roster yet. The press adds the person to the client as a respondent and sends the mail.";

export const NOT_INVITED =
  "The person could not be added, so nothing was sent. Press once more; if it repeats, tell LINNFOSS.";

/**
 * The database refused to create the identity (0023 §4: the invitation the
 * press recorded a moment ago was not found), which Supabase Auth reports as
 * a generic database error; or sign-ups are off in the Auth settings, which
 * is the same answer to the administrator. Anything else is reported as it is.
 */
export function isNotInvitedError(message: string): boolean {
  return /database error (saving|creating) new user|signups not allowed/i.test(message);
}

/**
 * The status of an invitation: the run's state, whether any answer exists,
 * nothing else. A finalized or published run has its report (the respondent's
 * submission evaluates and finalizes in one step).
 */
export function invitationStatus(run: Pick<Run, "state">, answers: number): InvitationStatus {
  switch (run.state) {
    case "draft":
    case "ready_for_review":
      return answers > 0 ? "answering" : "sent";
    case "finalized":
    case "published":
      return "report_ready";
    default:
      return "retired";
  }
}

export interface InvitationRow {
  runId: string;
  runTitle: string;
  assessorId: string | null;
  email: string | null;          // from the roster, by assessor id; null when unassigned
  instrumentVersionId: string;
  status: InvitationStatus;
  createdAt: string;
}

/** Join runs, assignments, the roster and the answer counts into the list, newest first. */
export function invitationRows(input: {
  runs: readonly Run[];
  assignments: readonly { run_id: string; assessor_id: string }[];
  roster: readonly MemberDirectoryEntry[];
  answers: ReadonlyMap<string, number>;
}): InvitationRow[] {
  const assessorOf = new Map(input.assignments.map((a) => [a.run_id, a.assessor_id]));
  const emailOf = new Map(input.roster.map((m) => [m.user_id, m.email]));
  return input.runs
    .slice()
    .sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0))
    .map((r) => {
      const assessorId = assessorOf.get(r.id) ?? null;
      return {
        runId: r.id,
        runTitle: r.title ?? "Untitled run",
        assessorId,
        email: assessorId ? (emailOf.get(assessorId) ?? null) : null,
        instrumentVersionId: r.instrument_version_id,
        status: invitationStatus(r, input.answers.get(r.id) ?? 0),
        createdAt: r.created_at,
      };
    });
}
